/**
 * FloatingSoftphone — the desktop Business Phone: a floating softphone
 * widget toggled from the workspace toolbar (phone button). Shares the SAME
 * RemodEngage data layer — every call it places lands in the one history.
 * Hidden by default; opens via the `remod:softphone-toggle` window event.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Phone, PhoneCall, PhoneOff, PhoneIncoming, PhoneOutgoing, X,
  MessageSquare, Delete, Loader2, Maximize2, Voicemail,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (raw) => {
  const d = (raw || '').replace(/\D/g, '').slice(0, 11);
  if (!d) return '';
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  if (d.length <= 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return `+${d.slice(0, 1)} (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
};

const e164 = (raw) => {
  const d = (raw || '').replace(/\D/g, '');
  if (!d) return '';
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith('1')) return `+${d}`;
  return raw.startsWith('+') ? raw : `+${d}`;
};

const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

const FloatingSoftphone = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [companyId, setCompanyId] = useState(() => localStorage.getItem('company_id') || '');
  const [number, setNumber] = useState(null);
  const [dial, setDial] = useState('');
  const [placing, setPlacing] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [recents, setRecents] = useState([]);
  const [incoming, setIncoming] = useState(null);
  const [takingOver, setTakingOver] = useState(false);
  const dismissedRef = React.useRef(new Set());

  useEffect(() => {
    const toggle = () => {
      setCompanyId(localStorage.getItem('company_id') || '');
      setOpen(o => !o);
    };
    const openIt = () => {
      setCompanyId(localStorage.getItem('company_id') || '');
      setOpen(true);
    };
    // Cross-system Call handoff (§10): open prefilled with a customer number.
    const dialTo = (e) => {
      const num = e?.detail?.number;
      setCompanyId(localStorage.getItem('company_id') || '');
      if (num) setDial(fmt(num));
      setOpen(true);
    };
    window.addEventListener('remod:softphone-toggle', toggle);
    window.addEventListener('remod:softphone-open', openIt);
    window.addEventListener('remod:softphone-dial', dialTo);
    return () => {
      window.removeEventListener('remod:softphone-toggle', toggle);
      window.removeEventListener('remod:softphone-open', openIt);
      window.removeEventListener('remod:softphone-dial', dialTo);
    };
  }, []);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const r = await axios.get(`${API}/addons/telnyx/number/${companyId}`);
      setNumber(r.data?.has_number ? r.data : null);
    } catch { setNumber(null); }
    try {
      const r = await axios.get(`${API}/voice/calls/${companyId}?limit=4`);
      setRecents(r.data?.calls || []);
    } catch { setRecents([]); }
  }, [companyId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // ── Incoming call screen-pop (baseline Business Phone UX) ──
  // Poll for live inbound calls; the backend attaches the caller's identity
  // and context from existing RemodDash data (customer / prospect / unknown).
  useEffect(() => {
    if (!companyId) return undefined;
    let on = true;
    const poll = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const r = await axios.get(`${API}/engage/${companyId}/live-calls`);
        if (!on) return;
        const live = (r.data?.calls || []).find(c => !dismissedRef.current.has(c.id));
        setIncoming(live || null);
      } catch { if (on) setIncoming(null); }
    };
    poll();
    const t = setInterval(poll, 7000);
    return () => { on = false; clearInterval(t); };
  }, [companyId]);

  const takeOver = async () => {
    if (!incoming) return;
    setTakingOver(true);
    try {
      await axios.post(`${API}/engage/${companyId}/live-calls/${incoming.id}/take-over`);
      toast.success('Bridging you in — your phone will ring now. Answer to take the call.');
      dismissedRef.current.add(incoming.id);
      setIncoming(null);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not bridge you into the call');
    } finally { setTakingOver(false); }
  };

  const dismissIncoming = () => {
    if (incoming) dismissedRef.current.add(incoming.id);
    setIncoming(null);
  };

  const close = () => setOpen(false);

  const placeCall = async (target) => {
    const to = e164(target || dial);
    if (!to) { toast.error('Enter a number to call'); return; }
    if (!number) { toast.error('You need an active business number first'); return; }
    setPlacing(true);
    try {
      const res = await axios.post(`${API}/voice/outbound/start`, {
        company_id: companyId, to_number: to, record: false,
      });
      if (res.data?.success && res.data?.voice_call) {
        setActiveCall(res.data.voice_call);
        toast.success('Calling — your phone will ring shortly');
        setDial('');
      } else toast.error('Could not place call');
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && typeof d === 'object' && d.detail) || (typeof d === 'string' ? d : 'Could not place call'));
    } finally { setPlacing(false); }
  };

  const hangup = async () => {
    if (activeCall?.id) {
      try { await axios.post(`${API}/voice/outbound/${activeCall.id}/hangup`); } catch { /* ignore */ }
    }
    setActiveCall(null);
    load();
  };

  if (!companyId) return null;

  const pop = incoming && (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-[26rem] md:w-[320px] z-[9995] rounded-2xl shadow-2xl border border-emerald-700/50 bg-slate-900 text-white overflow-hidden"
      data-testid="incoming-call-pop">
      <div className="flex items-center gap-2 px-4 py-2 bg-emerald-600/20 border-b border-emerald-700/40">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400" />
        </span>
        <p className="text-xs font-semibold text-emerald-300">
          Incoming call{incoming.answer_mode === 'ari' || incoming.state ? ' — Ari is answering' : ''}
        </p>
      </div>
      <div className="px-4 py-3">
        <p className="text-lg font-semibold truncate" data-testid="icp-name">
          {incoming.caller?.name || incoming.from_number || 'Unknown'}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${incoming.caller?.type === 'customer' ? 'bg-teal-500/20 text-teal-300' : incoming.caller?.type === 'prospect' ? 'bg-amber-500/20 text-amber-300' : 'bg-slate-600/40 text-slate-300'}`}
            data-testid="icp-badge">
            {incoming.caller?.badge || 'Unknown caller'}
          </span>
          {incoming.caller?.name && (
            <span className="text-[11px] text-slate-400 font-mono">{incoming.from_number}</span>
          )}
        </div>
        {(incoming.caller?.context || []).length > 0 && (
          <div className="mt-2 space-y-0.5" data-testid="icp-context">
            {incoming.caller.context.map((l, i) => (
              <p key={i} className="text-xs text-slate-300">{l}</p>
            ))}
          </div>
        )}
      </div>
      <div className="flex border-t border-slate-700">
        <button onClick={takeOver} disabled={takingOver || incoming.transfer_state === 'dialing_owner'}
          className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-semibold bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 transition-colors"
          data-testid="icp-takeover-btn">
          {takingOver ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneCall className="w-4 h-4" />}
          Take over
        </button>
        <button onClick={dismissIncoming}
          className="flex-1 py-2.5 text-sm text-slate-300 hover:bg-white/5 transition-colors"
          data-testid="icp-dismiss-btn">
          Let Ari handle
        </button>
      </div>
    </div>
  );

  if (!open) return pop || null;

  return (
    <>
    {pop}
    <div className="hidden md:flex fixed bottom-6 right-6 z-[9990] w-[300px] flex-col rounded-2xl shadow-2xl border border-slate-700 bg-slate-900 text-white overflow-hidden"
      data-testid="floating-softphone">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-800/80 border-b border-slate-700">
        <Phone className="w-4 h-4 text-emerald-400" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold">Business Phone</p>
          <p className="text-[10px] text-slate-400 font-mono truncate" data-testid="fs-number">
            {number?.phone_number || 'No business number'}
          </p>
        </div>
        <button onClick={() => { close(); navigate('/phone'); }}
          className="p-1.5 rounded-lg hover:bg-white/10" title="Open full phone app"
          data-testid="fs-open-full">
          <Maximize2 className="w-3.5 h-3.5 text-slate-400" />
        </button>
        <button onClick={close} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="fs-close-btn">
          <X className="w-4 h-4 text-slate-400" />
        </button>
      </div>

      {activeCall ? (
        <div className="p-5 flex flex-col items-center gap-4" data-testid="fs-active-call">
          <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center animate-pulse">
            <PhoneCall className="w-5 h-5 text-emerald-400" />
          </div>
          <div className="text-center">
            <p className="text-[10px] uppercase tracking-widest text-emerald-400">{activeCall.state || 'Calling'}</p>
            <p className="text-lg font-mono">{activeCall.to_number}</p>
          </div>
          <button onClick={hangup}
            className="w-12 h-12 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center"
            data-testid="fs-hangup-btn">
            <PhoneOff className="w-5 h-5" />
          </button>
        </div>
      ) : (
        <div className="p-4">
          <div className="flex items-center gap-1 mb-3">
            <input value={dial} readOnly placeholder="Dial a number"
              className="flex-1 bg-transparent text-center text-lg font-mono outline-none placeholder:text-slate-600"
              data-testid="fs-dial-input" />
            {dial && (
              <button onClick={() => setDial(fmt(dial.replace(/\D/g, '').slice(0, -1)))}
                className="p-1.5 text-slate-400 hover:text-white" data-testid="fs-backspace-btn">
                <Delete className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="grid grid-cols-3 gap-1.5 mb-3">
            {DIGITS.map(d => (
              <button key={d} onClick={() => setDial(fmt(dial + d))}
                className="h-10 rounded-lg bg-white/5 hover:bg-white/10 active:bg-white/20 text-base font-light"
                data-testid={`fs-digit-${d === '*' ? 'star' : d === '#' ? 'hash' : d}`}>
                {d}
              </button>
            ))}
          </div>
          <button onClick={() => placeCall()} disabled={placing || !dial}
            className="w-full h-10 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 flex items-center justify-center gap-2 text-sm font-semibold"
            data-testid="fs-call-btn">
            {placing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />} Call
          </button>
        </div>
      )}

      {/* Mini recents */}
      {!activeCall && recents.length > 0 && (
        <div className="px-4 pb-2 border-t border-slate-800 pt-2" data-testid="fs-recents">
          {recents.map(c => {
            const inbound = c.direction === 'inbound';
            const other = inbound ? c.from_number : c.to_number;
            const isVm = c.kind === 'voicemail_inbound' || c.outcome === 'voicemail_left';
            return (
              <button key={c.id} onClick={() => other && placeCall(other)}
                className="w-full flex items-center gap-2 py-1.5 hover:bg-white/5 rounded-lg px-1 text-left"
                data-testid={`fs-recent-${c.id}`}>
                {isVm ? <Voicemail className="w-3 h-3 text-purple-400 shrink-0" />
                  : inbound ? <PhoneIncoming className="w-3 h-3 text-blue-400 shrink-0" />
                    : <PhoneOutgoing className="w-3 h-3 text-emerald-400 shrink-0" />}
                <span className="text-xs font-mono truncate flex-1">{other || 'Unknown'}</span>
                <span className="text-[10px] text-slate-500 shrink-0">{c.duration_s ? `${c.duration_s}s` : ''}</span>
              </button>
            );
          })}
        </div>
      )}

      <button onClick={() => { close(); navigate('/messages'); }}
        className="flex items-center justify-center gap-2 py-2.5 bg-slate-800/60 hover:bg-slate-800 text-xs text-slate-300 border-t border-slate-700"
        data-testid="fs-open-messages">
        <MessageSquare className="w-3.5 h-3.5" /> Open RemodEngage — one history for calls &amp; texts
      </button>
    </div>
    </>
  );
};

export default FloatingSoftphone;
